export type JsonDataType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'
  | 'null';

/**
 * Formats a JSON value for display purposes.
 * Handles various data types and returns a human-readable string representation.
 *
 * @param value - The value to format (can be string, number, boolean, null, undefined, or complex objects)
 * @returns A formatted string representation of the value
 *
 * @example
 * formatJsonValue('hello') // Returns: "hello"
 * formatJsonValue(42) // Returns: 42
 * formatJsonValue(true) // Returns: true
 * formatJsonValue(null) // Returns: null
 * formatJsonValue({key: 'value'}) // Returns: JSON stringified object
 */
export function formatJsonValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  if (typeof value === 'boolean') {
    return value.toString();
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  return JSON.stringify(value, null, 2);
}

/**
 * Determines the data type of a JSON value.
 * Useful for JSON display components to categorize and style values appropriately.
 *
 * @param value - The value to check the type of
 * @returns The data type as a string literal type
 *
 * @example
 * getJsonDataType('hello') // Returns: 'string'
 * getJsonDataType(42) // Returns: 'number'
 * getJsonDataType(true) // Returns: 'boolean'
 * getJsonDataType(null) // Returns: 'null'
 * getJsonDataType([1, 2, 3]) // Returns: 'array'
 * getJsonDataType({key: 'value'}) // Returns: 'object'
 */
export function getJsonDataType(value: unknown): JsonDataType {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'object') {
    return 'object';
  }
  return typeof value as 'string' | 'number' | 'boolean';
}

/**
 * Cleans a JSON response string by removing markdown code blocks.
 * Useful when AI responses include ```json ... ``` wrappers.
 *
 * @param response - The raw response string that may contain markdown
 * @returns The cleaned response string without markdown code blocks
 *
 * @example
 * cleanJsonResponse('```json\n{"key": "value"}\n```') // Returns: '{"key": "value"}'
 */
export function cleanJsonResponse(response: string): string {
  let cleanedResponse = response.trim();
  cleanedResponse = cleanedResponse
    .replace(/```json\s*/g, '')
    .replace(/```\s*$/g, '')
    .trim();
  return cleanedResponse;
}

/**
 * Checks if a response string contains SMS/Email formatted data.
 * Supports multiple formats:
 * - Format 1: email_template + sms_template_1/sms_template_2
 * - Format 2: email + sms (array)
 *
 * @param response - The response string to check
 * @returns True if the response contains SMS/Email structure, false otherwise
 *
 * @example
 * isSmsEmailFormat('{"email": "...", "sms": [...]}') // Returns: true
 * isSmsEmailFormat('{"other": "data"}') // Returns: false
 */
export function isSmsEmailFormat(response: string): boolean {
  if (!response) {
    return false;
  }

  try {
    const cleanedResponse = cleanJsonResponse(response);
    const parsed = JSON.parse(cleanedResponse);

    // Check for either format:
    // Format 1: email_template + sms_template_1/sms_template_2
    // Format 2: email + sms (array)
    const hasEmailTemplate = !!parsed.email_template;
    const hasSmsTemplate = !!(parsed.sms_template_1 || parsed.sms_template_2);

    const hasEmail = !!parsed.email;
    const hasSms = !!(
      parsed.sms &&
      Array.isArray(parsed.sms) &&
      parsed.sms.length > 0
    );

    return (hasEmailTemplate && hasSmsTemplate) || (hasEmail && hasSms);
  } catch {
    return false;
  }
}

/**
 * Checks if a response string is valid JSON.
 *
 * @param response - The response string to validate
 * @returns True if the response is valid JSON, false otherwise
 *
 * @example
 * isValidJsonFormat('{"key": "value"}') // Returns: true
 * isValidJsonFormat('not json') // Returns: false
 */
export function isValidJsonFormat(response: string): boolean {
  if (!response) {
    return false;
  }

  try {
    const cleanedResponse = cleanJsonResponse(response);
    JSON.parse(cleanedResponse);
    return true;
  } catch {
    return false;
  }
}
